import React, { useCallback, useEffect, useRef, useState } from "react";
import {Link, useSearchParams} from 'react-router-dom';
import {
  Box, Button, Flex, Table, Tbody, Td, Th, Thead, Tr, Text, useToast, Spinner, Badge, Select,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter,
} from "@chakra-ui/react";
import { getReview, getReviewItem, resolveFlag, resolveAllFlags } from "../../utils/apis/agentApi";
import JevAssessments from "./JevAssessments";
import ReviewIssue, {conversationLink, issueNote} from './ReviewIssue';

/** Agent human-flag review queue: things the agent escalated to a person
 *  (out-of-scope, safety, customer reports…), with resolve. */
const FlagReview = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [busy, setBusy] = useState<number | "all" | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const requestVersion = useRef(0);
  const mutationInFlight = useRef(false);
  const toast = useToast();
  const [params] = useSearchParams();
  const flag = params.get('flag'), caseId = params.get('case');
  const [focused, setFocused] = useState<any>(null);
  const [detailError, setDetailError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRetry, setDetailRetry] = useState(0);
  const [error, setError] = useState('');
  useEffect(() => {
    let current = true;
    setFocused(null); setDetailError('');
    if (!flag && !caseId) {setDetailLoading(false); return;}
    setDetailLoading(true);
    getReviewItem(flag, caseId).then(result => {
      if (!current) return;
      setDetailLoading(false);
      if (result.ok && result.data) setFocused(result.data);
      else setDetailError('Could not load this issue. It may have been removed, or the service may be unavailable.');
    });
    return () => {current = false;};
  }, [flag, caseId, detailRetry]);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError('');
    const res = await getReview(200, resolved);
    if (version !== requestVersion.current) return;
    if (res.ok && Array.isArray(res.data)) setRows(res.data);
    else {setRows([]); setError('Review queue unavailable. Refresh to try again.');}
    setLoading(false);
  }, [resolved, toast]);
  useEffect(() => { load(); return () => { requestVersion.current += 1; }; }, [load]);

  const resolve = async (id: number) => {
    if (mutationInFlight.current) return;
    mutationInFlight.current = true;
    requestVersion.current += 1; // an earlier refresh cannot restore this row
    setBusy(id);
    const res = await resolveFlag(id);
    if (res.ok) {
      setRows(current => current.filter(r => r.id !== id));
      setFocused((current: any) => current?.id === id ? {...current, resolved: true} : current);
      toast({ title: "Resolved", status: "success", duration: 2000 });
    } else toast({ title: "Could not resolve this issue", description: "Please try again. It remains in the open queue.", status: "error", duration: 4000 });
    mutationInFlight.current = false;
    setBusy(null);
    setLoading(false);
  };

  const resolveAll = async () => {
    if (mutationInFlight.current || !rows.length) return;
    mutationInFlight.current = true;
    requestVersion.current += 1;
    setBusy("all");
    const throughId = Math.max(...rows.map(r => Number(r.id)));
    const res = await resolveAllFlags(throughId);
    if (res.ok) {
      setRows(current => current.filter(r => Number(r.id) > throughId));
      setConfirmAll(false);
      setDetailRetry(n => n + 1);
      toast({ title: `${res.data?.resolved_count ?? 0} issues resolved`, status: "success", duration: 3000 });
      await load(); // also shows genuinely new arrivals
    } else toast({ title: "Could not resolve the queue", description: "Please try again. No issues have been hidden.", status: "error", duration: 4000 });
    mutationInFlight.current = false;
    setBusy(null);
  };

  return (
    <Box p={{base: 3, md: 5}} minW={0}>
      <Flex wrap="wrap" gap={3} justifyContent="space-between" mb={4} alignItems="flex-start">
        <Box>
          <Text fontSize="xl" fontWeight="bold">Review queue</Text>
          <Text fontSize="sm" color="admin.muted">
            Chats the agent escalated to a human: a safety concern, a provider reporting
            a customer, or something outside what it can handle. Read the issue,
            open its conversation to act, and resolve it from either screen.
          </Text>
        </Box>
        <Flex gap={2} flexShrink={0} flexWrap="wrap">
          {!resolved && <Button colorScheme="purple" onClick={() => setConfirmAll(true)}
            isDisabled={loading || busy !== null || rows.length === 0}>Resolve all</Button>}
          <Button colorScheme="purple" variant="outline" onClick={load} isDisabled={loading || busy !== null}>Refresh</Button>
        </Flex>
      </Flex>
      {detailLoading && <Text mb={3}>Loading linked issue…</Text>}
      {detailError && <Box mb={3}><Text role="alert">{detailError}</Text><Button size="sm" onClick={() => setDetailRetry(n => n + 1)}>Retry issue</Button></Box>}
      {focused && <Box mb={4}><ReviewIssue key={focused.id || `case-${focused.case_id}`} issue={focused} onResolved={updated => {
        requestVersion.current += 1;
        setFocused(updated);
        setRows(current => resolved ? current.map(r => r.id === updated.id ? updated : r) : current.filter(r => r.id !== updated.id));
        setLoading(false);
      }} /></Box>}
      <Select aria-label="Review status" value={resolved ? "resolved" : "open"} maxW="240px" mb={4}
        isDisabled={busy !== null} onChange={e => { setRows([]); setResolved(e.target.value === "resolved"); }}>
        <option value="open">Open issues</option><option value="resolved">Resolved history</option>
      </Select>
      {loading ? <Spinner size="xl" /> : error ? <Text role="alert">{error}</Text> : rows.length === 0 ? (
        <Text color="admin.muted">{resolved ? "No resolved issues yet." : "Nothing awaiting review. 🎉"}</Text>
      ) : (
        <Box overflowX="auto" maxW="100%"><Table variant="striped">
          <Thead backgroundColor="#88077b"><Tr>
            <Th color="white">When</Th><Th color="white">Phone</Th><Th color="white">Reason</Th>
            <Th color="white">Note / context</Th><Th color="white">Action</Th>
          </Tr></Thead>
          <Tbody>
            {rows.map((r, i) => (
              <Tr key={r.id} backgroundColor={i % 2 === 0 ? "admin.surface" : "admin.selected"}>
                <Td whiteSpace="nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : "-"}</Td>
                <Td>{r.phone_number}</Td>
                <Td><Badge colorScheme="orange">{r.reason}</Badge></Td>
                <Td maxW="360px"><Text noOfLines={3} fontSize="sm">
                  {issueNote(r.context)}
                </Text><Button as={Link} to={`/flag-review?flag=${r.id}`} variant="link" size="sm">View full issue</Button></Td>
                <Td><Flex gap={2} wrap="wrap"><Button as={Link} to={conversationLink(r)} size="sm" variant="outline">Open conversation</Button>{resolved ? <Box><Badge colorScheme="green">Resolved</Badge>
                  {r.resolved_at && <Text fontSize="xs">{new Date(r.resolved_at).toLocaleString()}</Text>}
                  {r.resolution_note && <Text fontSize="xs">{r.resolution_note}</Text>}
                </Box> : <Button size="sm" colorScheme="purple" onClick={() => resolve(r.id)}
                  isLoading={busy === r.id} isDisabled={busy !== null}>Resolve</Button>}</Flex></Td>
              </Tr>
            ))}
          </Tbody>
        </Table></Box>
      )}
      <JevAssessments />
      <AlertDialog isOpen={confirmAll} leastDestructiveRef={cancelRef} onClose={() => { if (busy === null) setConfirmAll(false); }}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader>Resolve all open issues?</AlertDialogHeader>
          <AlertDialogBody>This clears the current open queue, including older issues beyond this page. Issues stay in Resolved history. New issues arriving after this refresh will stay open.</AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setConfirmAll(false)} isDisabled={busy !== null}>Cancel</Button>
            <Button colorScheme="purple" ml={3} onClick={resolveAll} isLoading={busy === "all"}>Resolve all issues</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
export default FlagReview;
